import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useEffect, useRef, useState } from 'react';

interface NotesPanelProps {
  isMobileView: boolean;
  notes: string;
  setNotes: (notes: string) => void;
  setActivePanel: (panel: 'chat' | 'notes' | 'annotate' | null) => void;
}

export function NotesPanel({
  isMobileView,
  notes,
  setNotes,
  setActivePanel,
}: NotesPanelProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [draft, setDraft] = useState(notes);
  const [charCount, setCharCount] = useState(notes.length);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // Track viewport height changes for keyboard detection
  useEffect(() => {
    const handleViewportResize = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      } else {
        setViewportHeight(window.innerHeight);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);

      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      };
    }
  }, []);

  // Keep draft in sync if parent changes (e.g., from remote updates later)
  useEffect(() => {
    setDraft(notes);
    setCharCount(notes.length);
  }, [notes]);

  // Debounce parent updates for better performance (500ms)
  useEffect(() => {
    setCharCount(draft.length);

    const id = setTimeout(() => {
      setNotes(draft);
    }, 500);

    return () => clearTimeout(id);
  }, [draft, setNotes]);

  // Auto-focus
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Layout constants - improved sizing, mobile optimized with keyboard adjustment
  const containerCommon =
    'bg-white flex flex-col rounded-2xl shadow-2xl border border-gray-300';
  const modalStyle = isMobileView
    ? {
        width: 'calc(100vw - 32px)', // Better margins on mobile
        maxWidth: '480px', // Wider on mobile
        height: `${Math.min(viewportHeight * 0.7, 600)}px`, // Dynamic height based on viewport
        maxHeight: `${viewportHeight - 100}px`, // Adjust for keyboard
      }
    : {
        width: '420px',
        maxWidth: '450px',
        height: '400px',
      };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
        onClick={() => setActivePanel(null)}
      />

      {/* Modal */}
      <div
        data-panel="notes"
        className={`fixed z-[60] ${containerCommon}`}
        style={{
          left: '50%',
          // Mobile: Center vertically when keyboard is closed, stick to top when keyboard is open
          top: isMobileView
            ? (viewportHeight < window.innerHeight * 0.8 ? '20px' : '50%')
            : 'auto',
          bottom: isMobileView ? 'auto' : '96px',
          transform: isMobileView
            ? (viewportHeight < window.innerHeight * 0.8 ? 'translateX(-50%)' : 'translate(-50%, -50%)')
            : 'translateX(-50%)',
          ...modalStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Improved styling */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="font-semibold text-gray-900 text-base">Call Notes</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActivePanel(null)}
            className="h-8 w-8 p-0 rounded-full hover:bg-white/50"
          >
            <X className="h-4 w-4 text-gray-700" />
          </Button>
        </div>

        {/* Notes Area - Improved */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5 bg-gradient-to-br from-gray-50 to-blue-50/30">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Jot down important points from this call..."
            className="w-full h-full p-3 md:p-4 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-900 placeholder-gray-400 resize-none shadow-sm transition-all"
            style={{
              fontSize: isMobileView ? '16px' : '15px', // 16px prevents zoom on iOS
              lineHeight: '1.7',
              minHeight: isMobileView ? '300px' : '250px',
            }}
          />
        </div>

        {/* Footer - Improved */}
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0 rounded-b-2xl">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-medium">{charCount} characters</span>
            <div className="flex items-center gap-1.5 text-green-600">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="font-medium">Auto-saved</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
